// ─── Event Names ────────────────────────────────────────────────────
// Single source of truth for all event names and domain topics.
// Format: DOMAIN.ACTION — always dot-separated, uppercase.
//
// Topics are grouped by domain (NEXUS.USERS, NEXUS.TENANTS, etc.).
// Event routing happens via the `eventType` message attribute, so
// adding new events never requires provisioning new Pub/Sub topics.
//
// Services use these constants when publishing/subscribing so typos
// are caught at compile time rather than at runtime.
//
// A new EventName or DomainTopics value also needs a human label on the
// Admin Events log — frontend/web/AGENTS.md, "No hardcoded user-facing text".

// ── Opportunities ───────────────────────────────────────────────────

export const OpportunityEvents = {
  GENERATE_NEW_SET_REQUESTED: "OPPORTUNITY.GENERATE_NEW_SET_REQUESTED",
  GENERATE_REQUESTED: "OPPORTUNITY.GENERATE_REQUESTED",
  STORYBOARD_GENERATION_REQUESTED:
    "OPPORTUNITY.STORYBOARD_GENERATION_REQUESTED",
  AI_EDIT_SCRIPT_REQUESTED: "OPPORTUNITY.AI_EDIT_SCRIPT_REQUESTED",
  SUGGEST_SCRIPT_REQUESTED: "OPPORTUNITY.SUGGEST_SCRIPT_REQUESTED",
  RESOLVE_REFERENCE_LINKS_REQUESTED:
    "OPPORTUNITY.RESOLVE_REFERENCE_LINKS_REQUESTED",
  GENERATED: "OPPORTUNITY.GENERATED",
  SCORED: "OPPORTUNITY.SCORED",
  APPROVED: "OPPORTUNITY.APPROVED",
  REJECTED: "OPPORTUNITY.REJECTED",
  BULK_REJECT_REQUESTED: "OPPORTUNITY.BULK_REJECT_REQUESTED",
  EXPORT_REQUESTED: "OPPORTUNITY.EXPORT_REQUESTED",
} as const;

export type OpportunityEventName =
  (typeof OpportunityEvents)[keyof typeof OpportunityEvents];

// ── Users ───────────────────────────────────────────────────────────

export const UserEvents = {
  REGISTERED: "USER.REGISTERED",
  UPDATED: "USER.UPDATED",
  DEACTIVATED: "USER.DEACTIVATED",
  REACTIVATED: "USER.REACTIVATED",
  ACCESS_GRANTED: "USER.ACCESS_GRANTED",
  ACCESS_REVOKED: "USER.ACCESS_REVOKED",
  ACCESS_BULK_GRANTED: "USER.ACCESS_BULK_GRANTED",
  ACCESS_BULK_REVOKED: "USER.ACCESS_BULK_REVOKED",
  ACCESS_REPLACED: "USER.ACCESS_REPLACED",
} as const;

export type UserEventName = (typeof UserEvents)[keyof typeof UserEvents];

// ── Tenants ─────────────────────────────────────────────────────────

export const TenantEvents = {
  PROVISIONED: "TENANT.PROVISIONED",
  UPDATED: "TENANT.UPDATED",
  DEACTIVATED: "TENANT.DEACTIVATED",
  /** Admin-initiated restore — clears `deletedAt` so the org is visible/usable again. */
  REACTIVATED: "TENANT.REACTIVATED",
  /** Bulk-import tenant settings: upsert industries, wire to tenant, update defaults. */
  IMPORT_SETTINGS: "TENANT.IMPORT_SETTINGS",
} as const;

export type TenantEventName = (typeof TenantEvents)[keyof typeof TenantEvents];

// ── Access Groups ───────────────────────────────────────────────────

export const AccessEvents = {
  CREATED: "ACCESS.CREATED",
  BULK_CREATED: "ACCESS.BULK_CREATED",
  UPDATED: "ACCESS.UPDATED",
  DELETED: "ACCESS.DELETED",
  /** Admin-initiated restore — clears `deletedAt` on a previously soft-deleted role. */
  RESTORED: "ACCESS.RESTORED",
  PERMISSIONS_UPDATED: "ACCESS.PERMISSIONS_UPDATED",
} as const;

export type AccessEventName = (typeof AccessEvents)[keyof typeof AccessEvents];

// ── Access Templates ────────────────────────────────────────────────

export const TemplateEvents = {
    CREATED: 'TEMPLATE.CREATED',
    UPDATED: 'TEMPLATE.UPDATED',
    STATUS_CHANGED: 'TEMPLATE.STATUS_CHANGED',
    GRANTED: 'TEMPLATE.GRANTED',
    REVOKED: 'TEMPLATE.REVOKED',
    /** Cold-assign a Wildcard role onto an org with no holders. */
    APPLIED_TO_ORG: 'TEMPLATE.APPLIED_TO_ORG',
    /** Soft-delete that projection while nobody holds it and no invite is pending. */
    REMOVED_FROM_ORG: 'TEMPLATE.REMOVED_FROM_ORG',
} as const;

export type TemplateEventName = (typeof TemplateEvents)[keyof typeof TemplateEvents];

// ── Invites ─────────────────────────────────────────────────────────
// Invite flows are synchronous (the API route does the DB write itself)
// — these events are fire-and-forget for audit-only consumers.

export const InviteEvents = {
  CREATED: "INVITE.CREATED",
  REGENERATED: "INVITE.REGENERATED",
  /** Same token, fresh email send — distinct from REGENERATED so audit can read "resent the email" vs "issued a new link". */
  RESENT: "INVITE.RESENT",
  /** Admin-revoked pending invite (token expired immediately, no acceptance possible). */
  REVOKED: "INVITE.REVOKED",
} as const;

export type InviteEventName = (typeof InviteEvents)[keyof typeof InviteEvents];

// ── System ──────────────────────────────────────────────────────────

export const SystemEvents = {
  HEALTH_PING: "SYSTEM.HEALTH_PING",
  /**
   * Asks a worker to turn a deploy's commit list into user-facing release
   * notes. Published by the API, which has already read GitHub and persisted
   * the changelog entry.
   */
  CHANGELOG_GENERATE_REQUESTED: "SYSTEM.CHANGELOG_GENERATE_REQUESTED",
  /**
   * Asks a worker to deliver approved release notes to the chosen channels.
   * Published by the API once an admin has approved the notes and picked
   * where they go.
   */
  CHANGELOG_DELIVER_REQUESTED: "SYSTEM.CHANGELOG_DELIVER_REQUESTED",
  /**
   * Asks a worker to merge the items an admin picked across several deploys
   * into one curated roundup. Separate from the per-deploy generation because
   * the input is a human's selection rather than a commit range.
   */
  CHANGELOG_DIGEST_GENERATE_REQUESTED:
    "SYSTEM.CHANGELOG_DIGEST_GENERATE_REQUESTED",
  /** Asks a worker to deliver an approved digest to the mailing list. */
  CHANGELOG_DIGEST_DELIVER_REQUESTED:
    "SYSTEM.CHANGELOG_DIGEST_DELIVER_REQUESTED",
  /**
   * Asks the API to look at GitHub for new production releases and to let
   * out any unattended changelog work that has come due (held Slack posts,
   * in-app notices, roundups). Published by Cloud Scheduler job
   * changelog-sync every ten minutes, which the API creates on boot when
   * SCHEDULER_BACKEND=gcp. The GitHub token lives on the API, so this event
   * is consumed there rather than on the users worker.
   */
  CHANGELOG_SYNC_REQUESTED: "SYSTEM.CHANGELOG_SYNC_REQUESTED",
  /**
   * Asks a worker to run the admin ops sweep: prune event and token-usage
   * history past their retention windows, and raise the Slack alerts the ops
   * settings ask for. Published by Cloud Scheduler on a cron, because an
   * alert that only evaluates when an admin opens a tab cannot fire on the
   * quiet weekend it exists for. Every write the sweep performs happens on
   * the worker, so the API routes that observe history stay read-only.
   */
  ADMIN_OPS_SWEEP_REQUESTED: "SYSTEM.ADMIN_OPS_SWEEP_REQUESTED",
} as const;

export type SystemEventName = (typeof SystemEvents)[keyof typeof SystemEvents];

// ── Auth ─────────────────────────────────────────────────────────────

export const AuthEvents = {
  LOGIN_SUCCEEDED: "AUTH.LOGIN_SUCCEEDED",
  LOGOUT: "AUTH.LOGOUT",
  MFA_ENABLED: "AUTH.MFA_ENABLED",
  MFA_DISABLED: "AUTH.MFA_DISABLED",
} as const;

export type AuthEventName = (typeof AuthEvents)[keyof typeof AuthEvents];

// ── Sync (Data Sync) ────────────────────────────────────────────────

export const SyncEvents = {
  ALL_REQUESTED: "DATA_SYNC.ALL_REQUESTED",
  TENANT_SYNC_REQUESTED: "DATA_SYNC.TENANT_SYNC_REQUESTED",
  TENANT_KPI_PRECACHE_REQUESTED: "DATA_SYNC.TENANT_KPI_PRECACHE_REQUESTED",
  /** Amber Path: read one tenant's Brand Brain from the Nexus onboarding record + core.brands_metadata. */
  BRAND_BRAIN_FETCH_REQUESTED: "DATA_SYNC.BRAND_BRAIN_FETCH_REQUESTED",
  /** Amber Path: read one tenant's Nexus onboarding record and read-only setup snapshot from Postgres. */
  TENANT_ONBOARDING_FETCH_REQUESTED: "DATA_SYNC.TENANT_ONBOARDING_FETCH_REQUESTED",
  /** Amber Path: create the onboarding via the client-context API, then save the Nexus record and setup snapshot. */
  TENANT_ONBOARDING_CREATE_REQUESTED: "DATA_SYNC.TENANT_ONBOARDING_CREATE_REQUESTED",
  /** Amber Path: update editable onboarding fields via the client-context API, then the Nexus record. */
  TENANT_ONBOARDING_EDIT_REQUESTED: "DATA_SYNC.TENANT_ONBOARDING_EDIT_REQUESTED",
  /** Amber Path, read-only: does this customer already exist in the onboarding BigQuery tables or Nexus onboarding copy? */
  CUSTOMER_PARTITION_CHECK_REQUESTED: "DATA_SYNC.CUSTOMER_PARTITION_CHECK_REQUESTED",
  /** Admin → Settings → System: refresh the selected tenants' Nexus onboarding records from BigQuery. Reads BigQuery, writes Postgres only. */
  TENANT_ONBOARDING_SYNC_REQUESTED: "DATA_SYNC.TENANT_ONBOARDING_SYNC_REQUESTED",
} as const;

export type SyncEventName = (typeof SyncEvents)[keyof typeof SyncEvents];

// ── Pre Flight ──────────────────────────────────────────────────────

export const PreflightEvents = {
  REPORT_REQUESTED: "PREFLIGHT.REPORT_REQUESTED",
  SCORE_ASSET: "PREFLIGHT.SCORE_ASSET",
  UPLOAD_CONFIRMED: "PREFLIGHT.UPLOAD_CONFIRMED",
  // Pre Flight – Scripts: async video-vs-script review pipeline. Hosted in the
  // preflight worker as its own event on the shared NEXUS.PREFLIGHT topic.
  SCRIPT_REVIEW_REQUESTED: "PREFLIGHT.SCRIPT_REVIEW_REQUESTED",
  // Pre Flight – Scripts: an AI comment was deleted WITH a reason — the worker
  // drafts a prompt-improvement suggestion for the ticket.
  SCRIPT_REVIEW_COMMENT_REJECTED: "PREFLIGHT.SCRIPT_REVIEW_COMMENT_REJECTED",
  // Pre Flight – Scripts: transcribe an uploaded video and pick which script in
  // the selected set it was made from (the creation form's auto-match).
  SCRIPT_MATCH_REQUESTED: "PREFLIGHT.SCRIPT_MATCH_REQUESTED",
  // Marketplace partner portal: transcribe a just-uploaded video and score how
  // far its voiceover strays from the order line's script, before submit.
  PARTNER_VOICEOVER_CHECK_REQUESTED: "PREFLIGHT.PARTNER_VOICEOVER_CHECK_REQUESTED",
} as const;

export type PreflightEventName =
  (typeof PreflightEvents)[keyof typeof PreflightEvents];

// ── Workspace ────────────────────────────────────────────────────────

export const WorkspaceEvents = {
  DATA_REFRESH_REQUESTED: "WORKSPACE.DATA_REFRESH_REQUESTED",
  DATA_REFRESHED: "WORKSPACE.DATA_REFRESHED",
  /** Lightweight filter-option lookups (group-by distinct values, my-brand filter
   *  tuples) — dispatched instead of the API calling BigQuery directly. Routes to
   *  NEXUS.WORKSPACE by the WORKSPACE prefix; no new topic. */
  OPTIONS_REQUESTED: "WORKSPACE.OPTIONS_REQUESTED",
} as const;

export type WorkspaceEventName =
  (typeof WorkspaceEvents)[keyof typeof WorkspaceEvents];

// ── Personas ─────────────────────────────────────────────────────────

export const PersonaEvents = {
  /** Amber Path: API asks the personas worker to discover the next batch of personas. */
  DISCOVER_REQUESTED: "PERSONA.DISCOVER_REQUESTED",
  /**
   * Published by the upstream DS analysis pipeline (not Nexus) once a Tenant's
   * regenerated personalities are ready in BigQuery. Preferred payload uses
   * `session_id` (onboarding batch UUID); `tenantId` alone remains a compat
   * path. Nexus documents/consumes this event only — see data-sync's
   * persona-feedback-ready handler, which re-syncs the Tenant then marks the
   * in-scope unprocessed RejectedPersona rows processed.
   */
  FEEDBACK_READY: "PERSONA.FEEDBACK_READY",
  /**
   * Published by Nexus when the reviewer hits "Go to my personas" — not "Finish
   * setup", which only commits the industry selection. Signals adge_backend / DS
   * to recalculate core personalities from unprocessed RejectedPersona rows, and
   * is skipped when the completion adds none since the tenant's last one.
   */
  ONBOARDING_COMPLETED: "PERSONA.ONBOARDING_COMPLETED",
  /**
   * Child EventJob for the core/brand persona re-sync that FEEDBACK_READY
   * runs in-process. Not published on Pub/Sub — recorded so the Events
   * action shows completed → ready → core → comp.
   */
  CORE_SYNC: "PERSONA.CORE_SYNC",
  /** Same as CORE_SYNC for the industry (comp) persona re-sync. */
  INDUSTRY_SYNC: "PERSONA.INDUSTRY_SYNC",
  /** Requests AI avatar generation (A2) for one persona; published on create when `avatarUploadId` is null. */
  AVATAR_GENERATION_REQUESTED: "PERSONA.AVATAR_GENERATION_REQUESTED",
} as const;

export type PersonaEventName =
  (typeof PersonaEvents)[keyof typeof PersonaEvents];

// ── Persona Opportunities (`persona_opportunities` flow) ─────────────
//
// Event *names* for the Create Opportunity Set flow (PRD).
// Payload shapes live in `events/persona-opportunities.ts` — pair each schema
// with a constant below.
//
// Add one entry here per async action that crosses service boundaries
// (e.g. API → worker). Format: `PERSONA_OPPORTUNITY.<VERB_NOUN>`.

export const PersonaOpportunityEvents = {
  /** Fired when the user taps Create & Generate. */
  GENERATE_SET_REQUESTED: "PERSONA_OPPORTUNITY.GENERATE_SET_REQUESTED",
  /** Fired when the user generates (or regenerates) scripts for persona opportunities. */
  GENERATE_SCRIPTS_REQUESTED: "PERSONA_OPPORTUNITY.GENERATE_SCRIPTS_REQUESTED",
  /** Fired when the user asks for three alternative hooks on one persona script. */
  GENERATE_HOOK_VARIATIONS_REQUESTED:
    "PERSONA_OPPORTUNITY.GENERATE_HOOK_VARIATIONS_REQUESTED",
  /** Fired when the user AI-edits one or more persona scripts. */
  AI_EDIT_SCRIPTS_REQUESTED: "PERSONA_OPPORTUNITY.AI_EDIT_SCRIPTS_REQUESTED",
  /** Fired when the user AI-edits one persona brief. */
  AI_EDIT_BRIEFS_REQUESTED: "PERSONA_OPPORTUNITY.AI_EDIT_BRIEFS_REQUESTED",
  /** Fired when the user generates a sketch storyboard for one persona script. */
  STORYBOARD_GENERATION_REQUESTED:
    "PERSONA_OPPORTUNITY.STORYBOARD_GENERATION_REQUESTED",
  /** Fired when the user submits Add your script (custom brief → N scripts). */
  SUGGEST_SCRIPTS_REQUESTED: "PERSONA_OPPORTUNITY.SUGGEST_SCRIPTS_REQUESTED",
  /** Fired when the user doubles down on a winning Post-Flight ad. */
  /** Fired when the user confirms Import (paste/file text → one PersonaScript). */
  IMPORT_SCRIPT_FROM_TEXT_REQUESTED:
    "PERSONA_OPPORTUNITY.IMPORT_SCRIPT_FROM_TEXT_REQUESTED",
  /** Fired when the user confirms Import brief (paste/file text → one PersonaBrief). */
  IMPORT_BRIEF_FROM_TEXT_REQUESTED:
    "PERSONA_OPPORTUNITY.IMPORT_BRIEF_FROM_TEXT_REQUESTED",
  /** Fired when the Suggest Script modal pre-fetches pasted reference URLs. */
  RESOLVE_REFERENCE_LINKS_REQUESTED:
    "PERSONA_OPPORTUNITY.RESOLVE_REFERENCE_LINKS_REQUESTED",
  /** Fired when a platform admin migrates one tenant's legacy opportunities into the hub. */
  MIGRATE_LEGACY_TENANT_REQUESTED:
    "PERSONA_OPPORTUNITY.MIGRATE_LEGACY_TENANT_REQUESTED",
} as const;

export type PersonaOpportunityEventName =
  (typeof PersonaOpportunityEvents)[keyof typeof PersonaOpportunityEvents];

// ── Beta Features ───────────────────────────────────────────────────
// Shared Beta Worker domain. One topic for every Beta Feature that has
// backend/business logic. Frontend-only features add no event here.
// New backend features add an event name, never a new topic.

export const BetaFeatureEvents = {
  // New backend features add an event name here, never a new topic.
} as const;

export type BetaFeatureEventName =
  (typeof BetaFeatureEvents)[keyof typeof BetaFeatureEvents];

// ── Aggregate ───────────────────────────────────────────────────────

export type EventName =
  | OpportunityEventName
  | UserEventName
  | TenantEventName
  | AccessEventName
  | TemplateEventName
  | SystemEventName
  | SyncEventName
  | AuthEventName
  | InviteEventName
  | PreflightEventName
  | WorkspaceEventName
  | MarketplaceEventName
  | PersonaEventName
  | PersonaOpportunityEventName
  | BetaFeatureEventName;

// ── Domain Topics ───────────────────────────────────────────────────
// Stable Pub/Sub topics — one per domain, prefixed with NEXUS to
// distinguish from other production topics in shared GCP projects.

export const MarketplaceEvents = {
  ORDER_SENT: "MARKETPLACE.ORDER_SENT",
  SCRIPT_PREFLIGHT_SUBMITTED: "MARKETPLACE.SCRIPT_PREFLIGHT_SUBMITTED",
  SCRIPT_REVISION_REQUESTED: "MARKETPLACE.SCRIPT_REVISION_REQUESTED",
  SCRIPT_COMMENT_ADDED: "MARKETPLACE.SCRIPT_COMMENT_ADDED",
  SCRIPT_SUBMITTED_FOR_FINAL_APPROVAL:
    "MARKETPLACE.SCRIPT_SUBMITTED_FOR_FINAL_APPROVAL",
  VENDOR_CONNECTED: "MARKETPLACE.VENDOR_CONNECTED",
  VENDOR_TERMS_UPDATED: "MARKETPLACE.VENDOR_TERMS_UPDATED",
  OVERDUE_ORDERS_SWEEP_REQUESTED: "MARKETPLACE.OVERDUE_ORDERS_SWEEP_REQUESTED",
} as const;

export type MarketplaceEventName =
  (typeof MarketplaceEvents)[keyof typeof MarketplaceEvents];

/**
 * Observability-only names written to `event_jobs` via `recordAdminEvent`.
 * Not published on Pub/Sub and not in `KNOWN_EVENT_NAMES`.
 */
export const MarketplaceAdminEvents = {
  ORDER_CREATED: "MARKETPLACE.ORDER.CREATED",
  ORDER_SENT: "MARKETPLACE.ORDER.SENT",
  ORDER_STATUS_CHANGED: "MARKETPLACE.ORDER.STATUS_CHANGED",
  ORDER_ETA_UPDATED: "MARKETPLACE.ORDER.ETA_UPDATED",
  ORDER_MARK_READY: "MARKETPLACE.ORDER.MARK_READY",
  ORDER_REVISION_REQUESTED: "MARKETPLACE.ORDER.REVISION_REQUESTED",
  ORDER_FINAL_APPROVAL: "MARKETPLACE.ORDER.FINAL_APPROVAL",
  ORDER_HANDOFF_SNAPSHOT_CREATED: "MARKETPLACE.ORDER.HANDOFF_SNAPSHOT_CREATED",
  PUBLISH_ATTEMPTED: "MARKETPLACE.PUBLISH.ATTEMPTED",
  PUBLISH_SUCCEEDED: "MARKETPLACE.PUBLISH.SUCCEEDED",
  PUBLISH_FAILED: "MARKETPLACE.PUBLISH.FAILED",
  PUBLISH_TIMEOUT: "MARKETPLACE.PUBLISH.TIMEOUT",
  PUBLISH_RETRIED: "MARKETPLACE.PUBLISH.RETRIED",
  PUBLISH_BEST_EFFORT_DROPPED: "MARKETPLACE.PUBLISH.BEST_EFFORT_DROPPED",
  NOTIFY_RECEIVED: "MARKETPLACE.NOTIFY.RECEIVED",
  NOTIFY_RECIPIENTS_RESOLVED: "MARKETPLACE.NOTIFY.RECIPIENTS_RESOLVED",
  NOTIFY_ZERO_RECIPIENTS_SKIPPED: "MARKETPLACE.NOTIFY.ZERO_RECIPIENTS_SKIPPED",
  NOTIFY_DELIVERY_FAILED: "MARKETPLACE.NOTIFY.DELIVERY_FAILED",
  EMAIL_SENT: "MARKETPLACE.EMAIL.SENT",
  EMAIL_FAILED: "MARKETPLACE.EMAIL.FAILED",
  EMAIL_SKIPPED: "MARKETPLACE.EMAIL.SKIPPED",
  PREFLIGHT_ATTEMPTED: "MARKETPLACE.PREFLIGHT.ATTEMPTED",
  PREFLIGHT_SUBMITTED: "MARKETPLACE.PREFLIGHT.SUBMITTED",
  PREFLIGHT_PUBLISH_LEG: "MARKETPLACE.PREFLIGHT.PUBLISH_LEG",
  PREFLIGHT_ROLLBACK: "MARKETPLACE.PREFLIGHT.ROLLBACK",
  PREFLIGHT_ACK_PERSIST_FAILED: "MARKETPLACE.PREFLIGHT.ACK_PERSIST_FAILED",
  PREFLIGHT_RESUBMIT: "MARKETPLACE.PREFLIGHT.RESUBMIT",
  PREFLIGHT_STORYBOARD_URL_ISSUED:
    "MARKETPLACE.PREFLIGHT.STORYBOARD_URL_ISSUED",
  SCRIPT_REVIEW_CREATED: "MARKETPLACE.SCRIPT_REVIEW.CREATED",
  SCRIPT_REVIEW_FAILED: "MARKETPLACE.SCRIPT_REVIEW.FAILED",
  SCRIPT_REVIEW_SHARE_MINTED: "MARKETPLACE.SCRIPT_REVIEW.SHARE_MINTED",
  SCRIPT_REVIEW_SHARE_REVOKED: "MARKETPLACE.SCRIPT_REVIEW.SHARE_REVOKED",
  SCRIPT_REVIEW_CLIENT_VIEWER_GET:
    "MARKETPLACE.SCRIPT_REVIEW.CLIENT_VIEWER_GET",
  SCRIPT_REVIEW_NOTE_POSTED: "MARKETPLACE.SCRIPT_REVIEW.NOTE_POSTED",
  IMPORT_BATCH_PROVISIONED: "MARKETPLACE.IMPORT.BATCH_PROVISIONED",
  IMPORT_COMMIT_STARTED: "MARKETPLACE.IMPORT.COMMIT_STARTED",
  IMPORT_COMMIT_FINISHED: "MARKETPLACE.IMPORT.COMMIT_FINISHED",
  IMPORT_RELEASE_SENT: "MARKETPLACE.IMPORT.RELEASE_SENT",
  IMPORT_RELEASE_SKIPPED: "MARKETPLACE.IMPORT.RELEASE_SKIPPED",
  IMPORT_RELEASE_RETRIED: "MARKETPLACE.IMPORT.RELEASE_RETRIED",
  IMPORT_FROZEN_LINK_RELEASED: "MARKETPLACE.IMPORT.FROZEN_LINK_RELEASED",
  VENDOR_CONNECTED: "MARKETPLACE.VENDOR.CONNECTED",
  VENDOR_TERMS_UPDATED: "MARKETPLACE.VENDOR.TERMS_UPDATED",
  VENDOR_CATALOG_CHANGED: "MARKETPLACE.VENDOR.CATALOG_CHANGED",
  VENDOR_RECIPIENTS_CHANGED: "MARKETPLACE.VENDOR.RECIPIENTS_CHANGED",
  AUTHZ_DENIED: "MARKETPLACE.AUTHZ.DENIED",
} as const;

export type MarketplaceAdminEventName =
  (typeof MarketplaceAdminEvents)[keyof typeof MarketplaceAdminEvents];

/** Observability-only display/filter registry consumed by the Admin Events tab. */
export const MARKETPLACE_ADMIN_EVENT_TYPES = Object.values(
  MarketplaceAdminEvents,
);

export const DomainTopics = {
  USERS: "NEXUS.USERS",
  TENANTS: "NEXUS.TENANTS",
  ACCESS: "NEXUS.ACCESS",
  OPPORTUNITIES: "NEXUS.OPPORTUNITIES",
  SYSTEM: "NEXUS.SYSTEM",
  DATA_SYNC: "NEXUS.DATA_SYNC",
  AUTH: "NEXUS.AUTH",
  INVITES: "NEXUS.INVITES",
  PREFLIGHT: "NEXUS.PREFLIGHT",
  WORKSPACE: "NEXUS.WORKSPACE",
  MARKETPLACE: "NEXUS.MARKETPLACE",
  PERSONAS: "NEXUS.PERSONAS",
  BETA_FEATURES: "NEXUS.BETA_FEATURES",
} as const;

export type DomainTopic = (typeof DomainTopics)[keyof typeof DomainTopics];

/**
 * Logical topic name for storyboard generation.
 *
 * NOTE: Pub/Sub routing in Nexus is done via DOMAIN topics
 * (`DomainTopics.OPPORTUNITIES`) + the `eventType` message attribute
 * (`OpportunityEvents.STORYBOARD_GENERATION_REQUESTED`) — adding a new
 * event never provisions a new Pub/Sub topic. This constant exists as
 * the stable logical identifier for the storyboard pipeline stage; it is
 * NOT a physical Pub/Sub topic. Publish/subscribe uses
 * `OpportunityEvents.STORYBOARD_GENERATION_REQUESTED`, which resolves to
 * `DomainTopics.OPPORTUNITIES` via `eventToDomainTopic`.
 */
export const STORYBOARD_GENERATION_TOPIC =
  "opportunity-storyboard-generation" as const;

/** Maps an event-name prefix to its domain topic. */
const PREFIX_TO_DOMAIN: Record<string, DomainTopic> = {
  USER: DomainTopics.USERS,
  TENANT: DomainTopics.TENANTS,
  ACCESS: DomainTopics.ACCESS,
  TEMPLATE: DomainTopics.ACCESS,
  OPPORTUNITY: DomainTopics.OPPORTUNITIES,
  SYSTEM: DomainTopics.SYSTEM,
  DATA_SYNC: DomainTopics.DATA_SYNC,
  AUTH: DomainTopics.AUTH,
  INVITE: DomainTopics.INVITES,
  PREFLIGHT: DomainTopics.PREFLIGHT,
  WORKSPACE: DomainTopics.WORKSPACE,
  MARKETPLACE: DomainTopics.MARKETPLACE,
  PERSONA: DomainTopics.PERSONAS,
  // `persona_opportunities` events publish to the opportunities domain topic.
  PERSONA_OPPORTUNITY: DomainTopics.OPPORTUNITIES,
  // Backend Beta Feature events only — frontend-only features publish nothing.
  BETA_FEATURE: DomainTopics.BETA_FEATURES,
};

// ── Known event catalog ─────────────────────────────────────────────

/**
 * Every event name in the catalog, flattened. This backs surfaces that let an
 * operator pick any real event — e.g. the publish dialog's from-scratch mode.
 * Unlike `TRIGGERABLE_EVENTS` it carries no payload schema: a consumer that
 * accepts one of these names accepts whatever payload comes with it.
 */
export const KNOWN_EVENT_NAMES: readonly EventName[] = [
  ...Object.values(OpportunityEvents),
  ...Object.values(UserEvents),
  ...Object.values(TenantEvents),
  ...Object.values(AccessEvents),
  ...Object.values(TemplateEvents),
  ...Object.values(InviteEvents),
  ...Object.values(SystemEvents),
  ...Object.values(AuthEvents),
  ...Object.values(SyncEvents),
  ...Object.values(PreflightEvents),
  ...Object.values(WorkspaceEvents),
  ...Object.values(MarketplaceEvents),
  ...Object.values(PersonaEvents).filter(
    (name) =>
      name !== PersonaEvents.CORE_SYNC && name !== PersonaEvents.INDUSTRY_SYNC,
  ),
  ...Object.values(PersonaOpportunityEvents),
  ...(Object.values(BetaFeatureEvents) as readonly EventName[]),
];

export function isKnownEventName(name: string): name is EventName {
  return (KNOWN_EVENT_NAMES as readonly string[]).includes(name);
}

/**
 * Resolves the domain topic for a given event name.
 * e.g. 'USER.REGISTERED' → 'NEXUS.USERS'
 */
export function eventToDomainTopic(eventName: string): DomainTopic {
  if (typeof eventName !== "string" || eventName.length === 0) {
    throw new Error(
      "eventToDomainTopic called with an empty event name — rebuild @nexus/contracts and restart services",
    );
  }
  const prefix = eventName.split(".")[0];
  const topic = PREFIX_TO_DOMAIN[prefix];
  if (!topic) {
    throw new Error(
      `Unknown event prefix "${prefix}" in event "${eventName}". Register it in PREFIX_TO_DOMAIN.`,
    );
  }
  return topic;
}
